import type { ComponentType } from 'react';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { PopupProps } from '@atlaskit/popup';
import {
	type GLOBAL_APP_MENU_ID,
	CONFIGURABLE_MENU_ID,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import type { EntryPointRouteParams } from '@atlassian/jira-entry-points-plugin/src/common/types.tsx';
import type { ReferenceFromEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';

export type AsyncAction = () => Promise<void>;

const OPERATIONS_MENU_ID = {
	OPERATIONS_ALERT_SAVED_SEARCH_SHARED_WITH_ME:
		'jira.sidebar.operations.alert.savedSearches.sharedWithMe',
	OPERATIONS_ALERT_SAVED_SEARCH_CREATED_BY_ME:
		'jira.sidebar.operations.alert.savedSearches.createdByMe',
} as const;

type ConfigurableMenuIdKey = keyof typeof CONFIGURABLE_MENU_ID;
export type ConfigurableMenuId = (typeof CONFIGURABLE_MENU_ID)[ConfigurableMenuIdKey];

type GlobalAppMenuIdKey = keyof typeof GLOBAL_APP_MENU_ID;
export type GlobalAppMenuId = (typeof GLOBAL_APP_MENU_ID)[GlobalAppMenuIdKey];

export type JiraConfigurableNavigationItem = {
	readonly menuId: string;
	readonly isVisible: boolean;
};

export const FILTERABLE_MENU_ID = {
	...CONFIGURABLE_MENU_ID,
	BOARDS: 'jira.sidebar.boards',
	QUEUES: 'jira.sidebar.queues',
	ALL: 'jira.sidebar.all',
} as const;

type FilterableMenuIdKey = keyof typeof FILTERABLE_MENU_ID;
export type FilterableMenuId = (typeof FILTERABLE_MENU_ID)[FilterableMenuIdKey];

export const L1_MENU_ID = {
	YOUR_WORK: 'jira.sidebar.yourWork',
	...CONFIGURABLE_MENU_ID,
	...OPERATIONS_MENU_ID,
} as const;

type MenuIdKey = keyof typeof L1_MENU_ID;
export type MenuId = (typeof L1_MENU_ID)[MenuIdKey];

// A nav menu button can appear in a flyout and may need access to its `onClose`.
export type NavMenuButtonProps = OnCloseProps;

// A collection of nav menu buttons can appear in a flyout and may need access to its `onClose`.
type NavMenuButtonsProps = OnCloseProps;

export type NavMenuButtons = ComponentType<NavMenuButtonsProps>;

// A nav submenu can appear in a flyout and may need access to its `onClose`.
type NavSubmenuFooterProps = OnCloseProps;

export type NavSubmenuFooter = ComponentType<NavSubmenuFooterProps>;
export type NavSubmenuHeaderProps = OnCloseProps;
export type NavSubmenuHeader = ComponentType<NavSubmenuHeaderProps>;
type NavSubmenuMenuItemsProps = OnCloseProps;
export type NavSubmenuMenuItems = ComponentType<NavSubmenuMenuItemsProps>;

export type OnClose = NonNullable<PopupProps['onClose']>;
export type OnCloseProps = { onClose?: OnClose };

// This is a temporary type used for filtering recent and starred results
export type NavigationFilters = {
	name: string;
	id: FilterableMenuId;
};

export type LeftSidebarEntryPointConfig = ReferenceFromEntryPoint<
	EntryPoint<EntryPointComponent<{}, Record<string, never>, {}, {}>, EntryPointRouteParams>
>;

export type MenuItemConfigData = {
	menuId: string;
	visible: boolean;
};
